// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import LoginScreen from "./screens/LoginScreen";
import MainScreen from "./screens/MainScreen";
import CommitteeDashboard from "./screens/CommitteeDashboard";
import MotionScreen from "./screens/MotionScreen";
import GlobalNotes from "./components/GlobalNotes";
import UserSheet from "./components/UserSheet";
import Footer from "./components/Footer";
import { FaMoon, FaSun } from "react-icons/fa";

// Utility for cookie management
function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = name + "=" + encodeURIComponent(value) + "; expires=" + expires + "; path=/";
}
function getCookie(name) {
  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1] || ""
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default to dark mode unless cookie says otherwise
  const [darkMode, setDarkMode] = useState(() => {
    const cookieValue = getCookie("darkMode");
    return cookieValue ? cookieValue === "true" : true;
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    setCookie("darkMode", darkMode ? "true" : "false");
  }, [darkMode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        {/* Dark mode toggle just above notes icon (closer to bottom) */}
        <button
          className="fixed bottom-24 right-6 z-50 p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow hover:scale-110 transition-all"
          style={{ fontSize: "1.2rem" }}
          onClick={() => setDarkMode((prev) => !prev)}
          aria-label="Toggle dark mode"
        >
          {darkMode ? <FaSun /> : <FaMoon />}
        </button>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={darkMode ? "dark" : ""}>
      {/* Dark mode toggle just above notes icon (closer to bottom) */}
      <button
        className="fixed bottom-24 right-6 z-50 p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow hover:scale-110 transition-all"
        style={{ fontSize: "1.2rem" }}
        onClick={() => setDarkMode((prev) => !prev)}
        aria-label="Toggle dark mode"
      >
        {darkMode ? <FaSun /> : <FaMoon />}
      </button>
      <Routes>
        {/* Public Route */}
        <Route
          path="/"
          element={user ? <Navigate to="/main" /> : <LoginScreen />}
        />
        {/* Protected Routes */}
        <Route
          path="/main"
          element={user ? <MainScreen /> : <Navigate to="/" />}
        />
        <Route
          path="/committee/:id"
          element={user ? <CommitteeDashboard /> : <Navigate to="/" />}
        />
        <Route
          path="/committee/:id/motion/:motionId"
          element={user ? <MotionScreen /> : <Navigate to="/" />}
        />
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* Show Global Notes ONLY when logged in (bottom-right) */}
      {user && <GlobalNotes />}

      {/* Show UserSheet ONLY when logged in (bottom-left) */}
      {user && (
        <div className="fixed bottom-6 left-6 z-50">
          <UserSheet />
        </div>
      )}

      {/* Footer at the bottom */}
      <Footer />
    </div>
  );
}

export default App;